'use client'

import { motion } from 'framer-motion'
import {
  CheckCircle,
  Users,
  Handshake,
  Globe,
  Package,
  Percent,
  Clock,
  Sparkles,
  TrendingUp,
  ShoppingBag,
  Star,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const iconMap: Record<string, LucideIcon> = {
  CheckCircle,
  Users,
  Handshake,
  Globe,
  Package,
  Percent,
  Clock,
  Sparkles,
  TrendingUp,
  ShoppingBag,
  Star,
  Zap,
}

interface StatsBarStat {
  value: string
  label: string
  icon?: string
}

interface StatsBarProps {
  stats: StatsBarStat[]
  variant?: 'primary' | 'ai' | 'marketplace' | 'bp'
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

export function StatsBar({ stats, variant = 'primary' }: StatsBarProps) {
  const isAi = variant === 'ai'
  const isMarketplace = variant === 'marketplace'

  const bgGradient = isAi
    ? 'bg-panel border-b border-line'
    : isMarketplace
      ? 'bg-gradient-to-r from-sky-600 to-sky-500'
      : 'bg-gradient-to-r from-primary-600 to-primary-500'

  const iconBg = isAi
    ? 'bg-brand-violet-soft'
    : 'bg-white/20'

  const iconColor = isAi ? 'text-brand-violet' : 'text-white'

  return (
    <section className={`py-6 md:py-8 ${bgGradient}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          key={variant}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="flex flex-wrap justify-center gap-x-6 gap-y-4 md:gap-x-8 lg:gap-x-10"
        >
          {stats.map((stat) => {
            const IconComponent = stat.icon ? iconMap[stat.icon] : CheckCircle

            return (
              <motion.div
                key={stat.label}
                variants={itemVariants}
                className={cn("flex items-center gap-2.5", isAi ? "text-ink" : "text-white")}
              >
                {IconComponent && (
                  <div className={cn('rounded-lg flex items-center justify-center w-8 h-8', iconBg)}>
                    <IconComponent className={cn('w-4 h-4', iconColor)} />
                  </div>
                )}
                <div>
                  <div className="font-bold tabular-nums text-lg md:text-xl">
                    {stat.value}
                  </div>
                  <div className={cn("text-xs", isAi ? "text-muted" : "text-white/80")}>
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}
